import i18n from '@/i18n';
import { format } from 'date-fns';
import {
  taskAPI,
} from '@/utils/api';
import {
  AI_COPYWRITING_MAX_CHARS_LIMIT,
  DEFAULT_AI_COPYWRITING_LANGUAGE,
  getCopywritingLanguageLabel,
  sanitizeCopywritingContent,
} from '@/pages/ipe/workbench/constants/aiCopywritingConfig';
import {
  buildAspectSizeApiPayload,
  DEFAULT_FISSION_ASPECT_SIZE,
  DEFAULT_OUTPAINT_EXPAND_PARAMS,
} from '@/pages/ipe/workbench/constants/imageFissionAspectSize';
import {
  extractUrlsFromFundingTask,
  waitForFundingTaskComplete,
} from './waitForFundingTask';
import {
  DEFAULT_MODEL_TRY_ON_ETHNICITY,
  DEFAULT_MODEL_TRY_ON_MODEL_TYPE,
  isModelStudioNav,
  sanitizeModelTryOnParams,
  resolveTryOnFrameScopeForGarment,
} from '@/pages/ipe/workbench/constants/modelStudioConfig';
import {
  resolveTryOnPresetFromParams,
  buildTryOnTaskModelParams,
  resolveTryOnPresetImageUrlForApi,
} from './tryOnSystemModelPresetFallback';
import {
  buildModelEthnicityTaskModelParams,
  resolveEthnicityPresetFeatureCode,
  resolveModelEthnicityPresetFromParams,
} from './systemModelEthnicityPresetCatalog';
import {
  buildAiStudioImageSuccessResult,
  markAiStudioImageTaskFailed,
  markAiStudioImageTaskSuccess,
} from './aiStudioImageTaskRecord';
import { resolveCopywritingTemplateContentById } from './aiCopywritingTemplateLoader';
import { resolveImg2ImgReferenceForApi } from './img2imgPublicUrl';
import { resolveImg2ImgWorkflowPrompt } from './img2imgPromptCaseLoader';
import { resolveText2ImgWorkflowParams } from './text2imgPromptCaseLoader';
import {
  beginWorkflowFeatureTask,
} from './workflowFeatureTask';
import { persistProcessedImageToSpace } from './sourcingWorkflowSpace';
import { runStudioWorkerWorkflowStep } from './studioWorkerWorkflow';
import { buildPipelineReferenceResult } from '@/pages/ipe/workbench/constants/fundingTaskImageUrlFields';

/** 工作流批量流水线已接入的 AI 图片类 navId */
export const WORKFLOW_AI_IMAGE_NAV_IDS = new Set([
  'img2img',
  'imageFission',
  'aiOutpaint',
  'aiTextToImage',
]);

export function isAiImageWorkflowNav(navId) {
  return WORKFLOW_AI_IMAGE_NAV_IDS.has(navId);
}

function readGenerateCount(params) {
  return Math.max(1, Math.min(4, Number(params?.generateCount) || 1));
}

async function resolvePipelineImageUrl(currentUrl) {
  const resolved = await resolveImg2ImgReferenceForApi(currentUrl);
  if (!resolved) throw new Error(i18n.t('workbench:ipeUtilError.pipelineImageUnresolved'));
  return resolved;
}

async function persistWorkflowOutputImages(row, navId, urls, labelPrefix) {
  const canonicalUrls = [];
  let primarySpaceId = null;
  let primaryCanonicalUrl = '';
  for (let i = 0; i < urls.length; i += 1) {
    const saved = await persistProcessedImageToSpace(
      `wf-${navId}-${row.id}-${i}`,
      urls[i],
      `${labelPrefix}-${i + 1}`,
      { navId },
    );
    canonicalUrls.push(saved.imageUrl);
    if (i === 0) {
      primarySpaceId = saved.spaceId;
      primaryCanonicalUrl = saved.imageUrl;
    }
  }
  return { primarySpaceId, primaryCanonicalUrl, canonicalUrls };
}

async function runModelImageWorkflowStep({
  navId,
  row,
  currentSpaceId,
  currentUrl,
  params = {},
  workflowContext = {},
  taskType,
  taskName,
  inputUrls,
  buildInitialResult,
}) {
  const generateCount = readGenerateCount(params);
  const estimatedTotal = Math.max(1, inputUrls.length * generateCount);
  const baseInitialResult = buildInitialResult({
    generateCount,
    inputUrls,
    currentUrl,
    currentSpaceId,
  });
  const { taskId, initialResult } = await beginWorkflowFeatureTask({
    taskType,
    taskName,
    imageIds: currentSpaceId ? [currentSpaceId] : [],
    previewUrl: baseInitialResult.previewUrl || inputUrls[0] || '',
    initialResult: {
      ...buildPipelineReferenceResult({
        imageUrl: inputUrls[0] || '',
        imageId: currentSpaceId,
        previewUrl: baseInitialResult.previewUrl || inputUrls[0] || currentUrl,
      }),
      ...baseInitialResult,
      serverWorker: true,
      batchPhase: 'queued',
    },
    estimatedTotal,
    workflowContext,
  });

  try {
    await taskAPI.submit(taskId);
    const task = await waitForFundingTaskComplete(taskId);
    const status = String(task?.status || '').toLowerCase();
    if (status === 'failed' || status === 'canceled' || status === 'cancelled') {
      const hint = task?.result?.failureHint || task?.result?.error;
      throw new Error(hint || i18n.t('workbench:ipeUtilError.taskFailedNamed', { name: taskName }));
    }
    const urls = extractUrlsFromFundingTask(task);
    if (urls.length === 0) {
      throw new Error(i18n.t('workbench:ipeUtilError.taskDoneNoImage', { name: taskName }));
    }
    const successResult = buildAiStudioImageSuccessResult({
      ...initialResult,
      ...(task?.result && typeof task.result === 'object' ? task.result : {}),
      engine: task?.result?.engine || 'qwen-image-2.0',
    }, task?.result || {}, urls);
    const finalResult = await markAiStudioImageTaskSuccess(taskId, urls, estimatedTotal, successResult, { navId });
    const finalUrls = finalResult.urls || urls;
    const persisted = await persistWorkflowOutputImages(row, navId, finalUrls, `工作流-${taskName}`);
    return {
      taskId,
      spaceId: persisted.primarySpaceId,
      imageUrl: persisted.primaryCanonicalUrl,
      outputUrls: finalUrls,
      compositeImageUrls: persisted.canonicalUrls,
      result: finalResult,
    };
  } catch (error) {
    await markAiStudioImageTaskFailed(taskId, initialResult, estimatedTotal, error);
    throw error;
  }
}

function resolveWorkflowModelEthnicity(params = {}) {
  return String(params.modelEthnicity || DEFAULT_MODEL_TRY_ON_ETHNICITY).trim() || DEFAULT_MODEL_TRY_ON_ETHNICITY;
}

async function resolveWorkflowCustomModelImage(params = {}, errorMessage = i18n.t('workbench:ipeUtilError.customModelUnresolved')) {
  if (params.usedUploadedModel !== true) return null;
  const modelUrl = String(params.modelImageUrl || '').trim();
  if (!modelUrl) return null;
  const resolvedModelImageUrl = await resolveImg2ImgReferenceForApi(modelUrl);
  if (!resolvedModelImageUrl) {
    throw new Error(errorMessage);
  }
  const previewUrl = String(params.modelImagePreviewUrl || modelUrl).trim();
  return {
    modelUrl,
    resolvedModelImageUrl,
    previewUrl,
    modelFields: buildTryOnTaskModelParams({
      uploadedModelRef: { apiUrl: modelUrl },
    }),
  };
}

async function resolveWorkflowSystemModelImage(systemPreset, errorMessage = i18n.t('workbench:ipeUtilError.systemModelUnresolvedWf')) {
  if (!systemPreset?.apiUrl && !systemPreset?.url) return null;
  const submitUrl = resolveTryOnPresetImageUrlForApi(systemPreset)
    || systemPreset.apiUrl
    || systemPreset.url;
  const resolvedModelImageUrl = await resolveImg2ImgReferenceForApi(submitUrl);
  if (!resolvedModelImageUrl) {
    throw new Error(errorMessage);
  }
  return {
    resolvedModelImageUrl,
    previewUrl: systemPreset.displayUrl || systemPreset.url || submitUrl,
    modelFields: buildTryOnTaskModelParams({ systemPreset }),
  };
}

/**
 * 解析模特工具步骤的载荷上下文（任务类型 / 名称 / initialResult 构造器）。
 * 单条目与「一个模块一个批次」共用，避免两套参数解析逻辑漂移。
 */
async function resolveModelStudioStepPayload({ navId, params = {} }) {
  const prompt = String(params.prompt || '').trim();
  const modelType = String(params.modelType || DEFAULT_MODEL_TRY_ON_MODEL_TYPE).trim() || DEFAULT_MODEL_TRY_ON_MODEL_TYPE;

  if (navId === 'modelTryOn') {
    const sanitized = sanitizeModelTryOnParams(params);
    const tryOnModelType = sanitized.modelType;
    const garmentType = sanitized.garmentType;
    const frameScope = String(sanitized.frameScope || '').trim() || resolveTryOnFrameScopeForGarment(garmentType);
    const uploadedModel = await resolveWorkflowCustomModelImage(params);
    let resolvedModelImageUrl = null;
    let modelFields = buildTryOnTaskModelParams();
    let systemPreset = null;
    let modelPreview = '';

    if (uploadedModel) {
      resolvedModelImageUrl = uploadedModel.resolvedModelImageUrl;
      modelFields = uploadedModel.modelFields;
      modelPreview = uploadedModel.previewUrl;
    } else {
      systemPreset = await resolveTryOnPresetFromParams(tryOnModelType, garmentType, params);
      const systemModel = await resolveWorkflowSystemModelImage(systemPreset);
      if (systemModel) {
        resolvedModelImageUrl = systemModel.resolvedModelImageUrl;
        modelFields = systemModel.modelFields;
        modelPreview = systemModel.previewUrl;
      }
    }

    return {
      taskType: 'modelTryOn',
      taskName: '模特试衣',
      buildInitialResult: ({ generateCount: count, inputUrls }) => ({
        prompt,
        generateCount: count,
        garmentType,
        frameScope,
        modelType: tryOnModelType,
        ...modelFields,
        selectedPresetId: systemPreset?.id || '',
        selectedPresetUrl: systemPreset?.url || params.selectedPresetUrl || '',
        garmentImageUrls: inputUrls,
        modelImageUrl: resolvedModelImageUrl || undefined,
        referencePreviews: [...inputUrls, modelPreview].filter(Boolean),
        previewUrl: inputUrls[0] || '',
        workflowBatch: true,
      }),
    };
  }

  if (navId === 'modelHandheld' || navId === 'modelUniversalWear') {
    const modelEthnicity = resolveWorkflowModelEthnicity(params);
    const featureCode = resolveEthnicityPresetFeatureCode(navId);
    const uploadedModel = await resolveWorkflowCustomModelImage(params);
    let resolvedModelImageUrl = null;
    let modelFields = buildModelEthnicityTaskModelParams();
    let systemPreset = null;
    let modelPreview = '';

    if (uploadedModel) {
      resolvedModelImageUrl = uploadedModel.resolvedModelImageUrl;
      modelFields = uploadedModel.modelFields;
      modelPreview = uploadedModel.previewUrl;
    } else {
      systemPreset = await resolveModelEthnicityPresetFromParams(
        featureCode,
        modelType,
        modelEthnicity,
        params,
      );
      const systemModel = await resolveWorkflowSystemModelImage(systemPreset);
      if (systemModel) {
        resolvedModelImageUrl = systemModel.resolvedModelImageUrl;
        modelFields = systemModel.modelFields;
        modelPreview = systemModel.previewUrl;
      }
    }

    const isHandheld = navId === 'modelHandheld';
    return {
      taskType: navId,
      taskName: isHandheld ? '手持商品' : '万物穿戴',
      buildInitialResult: ({ generateCount: count, inputUrls }) => ({
        prompt,
        generateCount: count,
        modelType,
        modelEthnicity,
        ...modelFields,
        selectedPresetId: systemPreset?.id || '',
        selectedPresetUrl: systemPreset?.url || params.selectedPresetUrl || '',
        ...(isHandheld
          ? { productImageUrls: inputUrls, productCount: inputUrls.length }
          : { garmentImageUrls: inputUrls }),
        modelImageUrl: resolvedModelImageUrl || undefined,
        referencePreviews: [...inputUrls, modelPreview].filter(Boolean),
        previewUrl: inputUrls[0] || '',
        workflowBatch: true,
      }),
    };
  }

  if (navId === 'modelPoseFission') {
    return {
      taskType: 'modelPoseFission',
      taskName: '姿势裂变',
      buildInitialResult: ({ generateCount: count, inputUrls }) => ({
        prompt,
        generateCount: count,
        hasCustomModel: true,
        modelImageUrls: inputUrls,
        modelImageUrl: inputUrls[0] || '',
        modelCount: inputUrls.length,
        referencePreviews: inputUrls,
        previewUrl: inputUrls[0] || '',
        workflowBatch: true,
      }),
    };
  }

  throw new Error(i18n.t('workbench:ipeUtilError.modelStepNotImplemented', { nav: navId }));
}

export async function runModelStudioWorkflowStep({
  navId,
  row,
  currentSpaceId,
  currentUrl,
  params = {},
  workflowContext = {},
}) {
  if (!isModelStudioNav(navId)) {
    throw new Error(i18n.t('workbench:ipeUtilError.modelStepUnsupported', { nav: navId }));
  }
  const inputUrl = await resolvePipelineImageUrl(currentUrl);
  const payload = await resolveModelStudioStepPayload({ navId, params });
  return runModelImageWorkflowStep({
    navId,
    row,
    currentSpaceId,
    currentUrl,
    params,
    workflowContext,
    taskType: payload.taskType,
    taskName: payload.taskName,
    inputUrls: [inputUrl],
    buildInitialResult: payload.buildInitialResult,
  });
}

export async function runAiImageWorkflowStep({
  navId,
  row,
  currentSpaceId,
  currentUrl,
  params = {},
  workflowContext = {},
}) {
  if (navId === 'img2img') {
    const inputUrl = await resolvePipelineImageUrl(currentUrl);
    const prompt = await resolveImg2ImgWorkflowPrompt(params);
    if (!prompt && !inputUrl) {
      throw new Error(i18n.t('workbench:ipeUtilError.img2imgNeedPrompt'));
    }
    return runModelImageWorkflowStep({
      navId,
      row,
      currentSpaceId,
      currentUrl,
      // 一图一生：固定每张参考图 1 张结果
      params: { ...params, prompt, generateCount: 1 },
      workflowContext,
      taskType: 'img2img',
      taskName: '万能改图',
      inputUrls: inputUrl ? [inputUrl] : [],
      buildInitialResult: ({ inputUrls }) => ({
        prompt,
        generateCount: 1,
        ...buildPipelineReferenceResult({
          imageUrl: inputUrls[0] || '',
          imageId: currentSpaceId,
          previewUrl: inputUrls[0] || currentUrl,
        }),
        generationMode: inputUrls.length > 0 ? 'img2img' : 'text2img',
        workflowBatch: true,
      }),
    });
  }

  if (navId === 'aiOutpaint') {
    const inputUrl = await resolvePipelineImageUrl(currentUrl);
    const expandParams = {
      ...DEFAULT_OUTPAINT_EXPAND_PARAMS,
      expandMode: Number(params.expandMode) === 2 ? 2 : 1,
      scale: params.scale ?? DEFAULT_OUTPAINT_EXPAND_PARAMS.scale,
      aspectRatio: params.aspectRatio || DEFAULT_OUTPAINT_EXPAND_PARAMS.aspectRatio,
      outputWidth: params.outputWidth ?? DEFAULT_OUTPAINT_EXPAND_PARAMS.outputWidth,
      outputHeight: params.outputHeight ?? DEFAULT_OUTPAINT_EXPAND_PARAMS.outputHeight,
    };
    return runModelImageWorkflowStep({
      navId,
      row,
      currentSpaceId,
      currentUrl,
      // 扩图不提供生成数量：固定每张原图 1 张结果
      params: { ...params, generateCount: 1 },
      workflowContext,
      taskType: 'aiOutpaint',
      taskName: 'AI扩图',
      inputUrls: [inputUrl],
      buildInitialResult: ({ inputUrls }) => ({
        expandParams,
        generateCount: 1,
        ...buildPipelineReferenceResult({
          imageUrl: inputUrls[0] || '',
          imageId: currentSpaceId,
          previewUrl: inputUrls[0] || currentUrl,
        }),
        workflowBatch: true,
      }),
    });
  }

  if (navId === 'aiTextToImage') {
    const { prompt, style, aspectRatio } = await resolveText2ImgWorkflowParams(params);
    if (!prompt) {
      throw new Error(i18n.t('workbench:ipeUtilError.text2imgNeedPrompt'));
    }
    const outputCount = readGenerateCount(params);
    const { taskId, initialResult } = await beginWorkflowFeatureTask({
      taskType: 'aiTextToImage',
      taskName: '文生图',
      imageIds: currentSpaceId ? [currentSpaceId] : [],
      previewUrl: currentUrl || '',
      estimatedTotal: outputCount,
      workflowContext,
      initialResult: {
        prompt,
        style,
        aspectRatio,
        outputCount,
        referencePreviews: [currentUrl].filter(Boolean),
        previewUrl: currentUrl || '',
        serverWorker: true,
        batchPhase: 'queued',
        outputs: [],
        urls: [],
      },
    });
    try {
      await taskAPI.submit(taskId);
      const task = await waitForFundingTaskComplete(taskId);
      const status = String(task?.status || '').toLowerCase();
      if (status === 'failed' || status === 'canceled' || status === 'cancelled') {
        throw new Error(task?.result?.failureHint || i18n.t('workbench:ipeUtilError.text2imgFailed'));
      }
      const urls = extractUrlsFromFundingTask(task);
      if (urls.length === 0) throw new Error(i18n.t('workbench:ipeUtilError.text2imgDoneNoImage'));
      const successResult = buildAiStudioImageSuccessResult({
        ...initialResult,
        engine: task?.result?.engine || 'qwen-image-2.0',
      }, task?.result || {}, urls);
      const finalResult = await markAiStudioImageTaskSuccess(taskId, urls, outputCount, successResult, { navId: 'aiTextToImage' });
      const finalUrls = finalResult.urls || urls;
      const persisted = await persistWorkflowOutputImages(row, navId, finalUrls, '工作流-文生图');
      return {
        taskId,
        spaceId: persisted.primarySpaceId,
        imageUrl: persisted.primaryCanonicalUrl,
        outputUrls: finalUrls,
        compositeImageUrls: persisted.canonicalUrls,
        result: finalResult,
      };
    } catch (error) {
      await markAiStudioImageTaskFailed(taskId, initialResult, outputCount, error);
      throw error;
    }
  }

  if (navId === 'imageFission') {
    const variationCount = Math.max(1, Math.min(4, Number(params.variationCount) || 1));
    const aspectPayload = buildAspectSizeApiPayload({
      aspectRatio: params.aspectRatio || DEFAULT_FISSION_ASPECT_SIZE.aspectRatio,
      outputWidth: params.outputWidth ?? params.customAspectWidth,
      outputHeight: params.outputHeight ?? params.customAspectHeight,
    });
    return runStudioWorkerWorkflowStep({
      navId: 'imageFission',
      taskType: 'imageFission',
      taskName: '印花裂变',
      row,
      currentSpaceId,
      currentUrl,
      workflowContext,
      estimatedTotal: variationCount,
      persistLabel: '工作流-印花裂变',
      initialResult: {
        ...aspectPayload,
        variationCount,
        patternOnly: params.patternOnly === true,
        billingNavId: params.patternOnly === true ? 'patternFission' : 'imageFission',
      },
    });
  }

  throw new Error(i18n.t('workbench:ipeUtilError.aiImageStepNotImplemented', { nav: navId }));
}

export async function runAiCopywritingWorkflowStep(row, currentSpaceId, currentUrl, params = {}, workflowContext = {}) {
  // 兼容旧工作流：titlePrompt → prompt；固定只生成 1 条
  const titlePresetId = String(params.titlePresetId || '').trim() || undefined;
  let prompt = String(params.prompt || params.titlePrompt || '').trim();
  if (!prompt && titlePresetId) {
    prompt = await resolveCopywritingTemplateContentById(titlePresetId);
  }
  const outputCount = 1;
  const language = String(params.copywritingLanguage || DEFAULT_AI_COPYWRITING_LANGUAGE).trim() || DEFAULT_AI_COPYWRITING_LANGUAGE;
  const maxCharsRaw = Number(String(params.maxChars ?? '').replace(/\D/g, ''));
  const maxChars = Number.isFinite(maxCharsRaw) && maxCharsRaw > 0
    ? Math.max(1, Math.min(AI_COPYWRITING_MAX_CHARS_LIMIT, Math.floor(maxCharsRaw)))
    : undefined;
  const uploadMode = String(params.uploadMode || 'default').trim().toLowerCase() === 'custom' ? 'custom' : 'default';
  const referenceUrl = await resolvePipelineImageUrl(currentUrl);
  const { taskId, initialResult } = await beginWorkflowFeatureTask({
    taskType: 'aiCopywriting',
    taskName: 'AI文案',
    imageIds: currentSpaceId ? [currentSpaceId] : [],
    previewUrl: currentUrl || referenceUrl,
    estimatedTotal: outputCount,
    workflowContext,
    initialResult: {
      prompt,
      titlePresetContent: prompt,
      titlePresetId,
      uploadMode,
      ...buildPipelineReferenceResult({
        imageUrl: referenceUrl,
        imageId: currentSpaceId,
        previewUrl: currentUrl || referenceUrl,
      }),
      generationMode: 'vision',
      outputCount,
      language,
      languageLabel: getCopywritingLanguageLabel(language),
      ...(maxChars ? { maxChars } : {}),
      content: '',
      text: '',
      serverWorker: true,
      batchPhase: 'queued',
    },
  });

  try {
    await taskAPI.submit(taskId);
    const task = await waitForFundingTaskComplete(taskId);
    const status = String(task?.status || '').toLowerCase();
    if (status === 'failed' || status === 'canceled' || status === 'cancelled') {
      throw new Error(task?.result?.failureHint || i18n.t('workbench:ipeUtilError.aiCopyFailed'));
    }
    let content = String(task?.result?.content || task?.result?.text || '').trim();
    if (!content) throw new Error(i18n.t('workbench:ipeUtilError.aiCopyDoneNoContent'));
    content = sanitizeCopywritingContent(content);
    const successResult = {
      ...initialResult,
      ...(task?.result && typeof task.result === 'object' ? task.result : {}),
      content,
      text: content,
      engine: task?.result?.engine || initialResult.engine || 'qwen',
    };
    await taskAPI.update(taskId, {
      status: 'success',
      successCount: 1,
      failedCount: 0,
      total: outputCount,
      finishedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      result: successResult,
    });
  return {
    taskId,
    copywritingText: content,
    language,
    note: content.length > 120 ? `${content.slice(0, 120)}…` : content,
  };
  } catch (error) {
    await taskAPI.update(taskId, {
      status: 'failed',
      successCount: 0,
      failedCount: outputCount,
      finishedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      result: {
        ...initialResult,
        failureHint: error?.message || String(error),
      },
    });
    throw error;
  }
}

/* ---------------------------------------------------------------------------
 * 「一个模块一个批次」批量执行
 * 一个工作流任务在同一模块里只创建一个批次任务：把全部条目作为该批次的多条
 * source 一起提交，跑完按 sourceIndex 把产出切回各条目。
 * ------------------------------------------------------------------------- */

/** 把任务产出的 urls 按 sourceIndex（缺省时按 index/perSource 推算）切回各条 source。 */
export function groupOutputUrlsBySource(result, count, perSource = 1) {
  const buckets = Array.from({ length: count }, () => []);
  const outputs = Array.isArray(result?.outputs) ? result.outputs : [];
  outputs.forEach((row, idx) => {
    const rowStatus = String(row?.status || '').toLowerCase();
    if (rowStatus === 'failed' || rowStatus === 'processing') return;
    const url = String(row?.url || row?.outputUrl || row?.imageUrl || '').trim();
    if (!url) return;
    let si = Number(row?.sourceIndex);
    if (!Number.isFinite(si)) {
      const flat = Number(row?.index);
      if (Number.isFinite(flat)) si = Math.floor(flat / Math.max(1, perSource));
      else si = idx;
    }
    if (!Number.isFinite(si) || si < 0 || si >= count) si = Math.max(0, Math.min(count - 1, si || 0));
    buckets[si].push(url);
  });
  if (!buckets.some((list) => list.length > 0)) {
    const all = extractUrlsFromFundingTask(result);
    all.forEach((u, i) => {
      if (!u) return;
      const si = Math.max(0, Math.min(count - 1, Math.floor(i / Math.max(1, perSource))));
      buckets[si].push(u);
    });
  }
  return buckets;
}

/**
 * 通用批量 Worker：一个任务承载 N 条 source，提交后等待完成，再按 sourceIndex 切回。
 * @param {{rowId: (string|number), spaceId: string, url: string}[]} sources
 */
async function runBatchWorkerStep({
  navId,
  taskType,
  taskName,
  sources = [],
  params = {},
  workflowContext = {},
  buildInitialResult,
  persistLabel,
  perSource,
}) {
  const inputUrls = sources.map((s) => s.url).filter(Boolean);
  const generateCount = Number.isFinite(perSource) ? perSource : readGenerateCount(params);
  const estimatedTotal = Math.max(1, inputUrls.length * Math.max(1, generateCount));
  const baseInitialResult = buildInitialResult({ generateCount, inputUrls, sources });
  const { taskId, initialResult } = await beginWorkflowFeatureTask({
    taskType,
    taskName,
    imageIds: sources.map((s) => s.spaceId).filter(Boolean),
    previewUrl: baseInitialResult.previewUrl || inputUrls[0] || '',
    estimatedTotal,
    workflowContext,
    initialResult: {
      ...buildPipelineReferenceResult({
        imageUrl: inputUrls[0] || '',
        imageId: sources[0]?.spaceId,
        previewUrl: baseInitialResult.previewUrl || inputUrls[0] || '',
      }),
      referenceUrls: inputUrls,
      referencePreviews: Array.isArray(baseInitialResult.referencePreviews)
        && baseInitialResult.referencePreviews.length > 0
        ? baseInitialResult.referencePreviews
        : inputUrls,
      ...baseInitialResult,
      serverWorker: true,
      batchPhase: 'queued',
      outputs: [],
      urls: [],
    },
  });

  try {
    await taskAPI.submit(taskId);
    const task = await waitForFundingTaskComplete(taskId);
    const status = String(task?.status || '').toLowerCase();
    if (status === 'failed' || status === 'canceled' || status === 'cancelled') {
      const hint = task?.result?.failureHint || task?.result?.error;
      throw new Error(typeof hint === 'string' && hint.trim() ? hint.trim() : i18n.t('workbench:ipeUtilError.taskFailedNamed', { name: taskName }));
    }
    const buckets = groupOutputUrlsBySource(task?.result || {}, sources.length, Math.max(1, generateCount));
    const bySource = [];
    for (let i = 0; i < sources.length; i += 1) {
      const list = buckets[i];
      if (!list.length) {
        bySource.push({
          rowId: sources[i].rowId,
          taskId,
          failed: true,
          error: i18n.t('workbench:ipeUtilError.rowNoImageOutput'),
        });
        continue;
      }
      const persisted = await persistWorkflowOutputImages(
        { id: sources[i].rowId ?? i },
        navId,
        list,
        persistLabel || `工作流-${taskName}`,
      );
      bySource.push({
        rowId: sources[i].rowId,
        taskId,
        spaceId: persisted.primarySpaceId,
        imageUrl: persisted.primaryCanonicalUrl,
        compositeImageUrls: persisted.canonicalUrls,
      });
    }
    const allUrls = buckets.flat();
    const successResult = buildAiStudioImageSuccessResult(
      {
        ...initialResult,
        ...(task?.result && typeof task.result === 'object' ? task.result : {}),
        engine: task?.result?.engine || 'qwen-image-2.0',
      },
      task?.result || {},
      allUrls,
    );
    await markAiStudioImageTaskSuccess(taskId, allUrls, estimatedTotal, successResult, { navId });
    return { taskId, bySource };
  } catch (error) {
    await markAiStudioImageTaskFailed(taskId, initialResult, estimatedTotal, error);
    throw error;
  }
}

/** 万能改图 / AI扩图：一个批次任务承载 N 条参考图。 */
export async function runAiImageBatchWorkflowStep({ navId, sources = [], params = {}, workflowContext = {} }) {
  const inputUrls = [];
  for (const source of sources) {
    inputUrls.push(await resolvePipelineImageUrl(source.url));
  }
  if (inputUrls.length === 0) throw new Error(i18n.t('workbench:ipeUtilError.missingInputImage'));

  if (navId === 'img2img') {
    const prompt = await resolveImg2ImgWorkflowPrompt(params);
    if (!prompt) {
      throw new Error(i18n.t('workbench:ipeUtilError.img2imgNeedPrompt'));
    }
    return runBatchWorkerStep({
      navId,
      taskType: 'img2img',
      taskName: '万能改图',
      sources,
      params: { ...params, generateCount: 1 },
      workflowContext,
      perSource: 1,
      persistLabel: '工作流-万能改图',
      buildInitialResult: ({ inputUrls: urls }) => ({
        prompt,
        generateCount: 1,
        // 多参考图必须显式开启批量模式，否则 worker 只取第一张
        batchMode: true,
        generationMode: urls.length > 0 ? 'img2img' : 'text2img',
        referenceUrls: urls,
        referencePreviews: urls,
        previewUrl: urls[0] || '',
        workflowBatch: true,
      }),
    });
  }

  if (navId === 'aiOutpaint') {
    const expandParams = {
      ...DEFAULT_OUTPAINT_EXPAND_PARAMS,
      expandMode: Number(params.expandMode) === 2 ? 2 : 1,
      scale: params.scale ?? DEFAULT_OUTPAINT_EXPAND_PARAMS.scale,
      aspectRatio: params.aspectRatio || DEFAULT_OUTPAINT_EXPAND_PARAMS.aspectRatio,
      outputWidth: params.outputWidth ?? DEFAULT_OUTPAINT_EXPAND_PARAMS.outputWidth,
      outputHeight: params.outputHeight ?? DEFAULT_OUTPAINT_EXPAND_PARAMS.outputHeight,
    };
    return runBatchWorkerStep({
      navId,
      taskType: 'aiOutpaint',
      taskName: 'AI扩图',
      sources,
      params: { ...params, generateCount: 1 },
      workflowContext,
      perSource: 1,
      persistLabel: '工作流-AI扩图',
      buildInitialResult: ({ inputUrls: urls }) => ({
        expandParams,
        generateCount: 1,
        referenceUrls: urls,
        referencePreviews: urls,
        previewUrl: urls[0] || '',
        workflowBatch: true,
      }),
    });
  }

  throw new Error(i18n.t('workbench:ipeUtilError.batchImageStepUnsupported', { nav: navId }));
}

/** 文生图：一个批次任务承载 N 条条目的参考图，每条各生成 generateCount 张。 */
export async function runAiTextToImageBatchWorkflowStep({ sources = [], params = {}, workflowContext = {} }) {
  const { prompt, style, aspectRatio } = await resolveText2ImgWorkflowParams(params);
  if (!prompt) {
    throw new Error(i18n.t('workbench:ipeUtilError.text2imgNeedPrompt'));
  }
  const outputCount = readGenerateCount(params);
  // 文生图不依赖原图，参考图解析失败也保留占位，避免各条目与 imageIds 顺序错位
  const referenceUrls = [];
  for (const source of sources) {
    const ref = await resolvePipelineImageUrl(source.url).catch(() => '');
    referenceUrls.push(ref || '');
  }
  const previewUrls = referenceUrls.filter(Boolean);
  return runBatchWorkerStep({
    navId: 'aiTextToImage',
    taskType: 'aiTextToImage',
    taskName: '文生图',
    sources,
    params,
    workflowContext,
    perSource: outputCount,
    persistLabel: '工作流-文生图',
    buildInitialResult: ({ inputUrls }) => ({
      prompt,
      style,
      aspectRatio,
      outputCount,
      engine: 'qwen-image-2.0',
      referenceUrls,
      referencePreviews: previewUrls.length ? previewUrls : inputUrls,
      previewUrl: previewUrls[0] || inputUrls[0] || '',
      workflowBatch: true,
    }),
  });
}

/** 图片裂变 / 图案裂变：一个批次任务承载 N 条条目的原图，每条各裂变 variationCount 张。 */
export async function runFissionBatchWorkflowStep({ sources = [], params = {}, workflowContext = {} }) {
  const taskName = params.patternOnly === true ? '图案裂变' : '印花裂变';
  const variationCount = Math.max(1, Math.min(4, Number(params.variationCount) || 1));
  const aspectPayload = buildAspectSizeApiPayload({
    aspectRatio: params.aspectRatio || DEFAULT_FISSION_ASPECT_SIZE.aspectRatio,
    outputWidth: params.outputWidth ?? params.customAspectWidth,
    outputHeight: params.outputHeight ?? params.customAspectHeight,
  });
  const referenceUrls = [];
  for (const source of sources) {
    const ref = await resolvePipelineImageUrl(source.url);
    if (ref) referenceUrls.push(ref);
  }
  if (referenceUrls.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.taskMissingSourceNamed', { name: taskName }));
  }
  return runBatchWorkerStep({
    navId: 'imageFission',
    taskType: 'imageFission',
    taskName,
    sources,
    params,
    workflowContext,
    perSource: variationCount,
    persistLabel: `工作流-${taskName}`,
    buildInitialResult: ({ inputUrls }) => ({
      ...aspectPayload,
      variationCount,
      patternOnly: params.patternOnly === true,
      billingNavId: params.patternOnly === true ? 'patternFission' : 'imageFission',
      referenceUrls,
      referencePreviews: referenceUrls,
      previewUrl: referenceUrls[0] || inputUrls[0] || '',
      workflowBatch: true,
    }),
  });
}

/** 模特工具（试衣/手持/穿戴/姿势裂变）：一个批次任务承载 N 条商品图。 */
export async function runModelStudioBatchWorkflowStep({ navId, sources = [], params = {}, workflowContext = {} }) {
  if (!isModelStudioNav(navId)) {
    throw new Error(i18n.t('workbench:ipeUtilError.modelStepUnsupported', { nav: navId }));
  }
  const inputUrls = [];
  for (const source of sources) {
    inputUrls.push(await resolvePipelineImageUrl(source.url));
  }
  if (inputUrls.length === 0) throw new Error(i18n.t('workbench:ipeUtilError.missingInputImage'));
  const payload = await resolveModelStudioStepPayload({ navId, params });
  const generateCount = readGenerateCount(params);
  return runBatchWorkerStep({
    navId,
    taskType: payload.taskType,
    taskName: payload.taskName,
    sources,
    params,
    workflowContext,
    perSource: generateCount,
    persistLabel: `工作流-${payload.taskName}`,
    buildInitialResult: payload.buildInitialResult,
  });
}

/**
 * AI文案：一个批次任务承载 N 组参考图（uploadMode=custom），
 * worker 按组生成并以空行拼接，前端再按空行切回各条目。
 */
export async function runAiCopywritingBatchWorkflowStep({ sources = [], params = {}, workflowContext = {} }) {
  const titlePresetId = String(params.titlePresetId || '').trim() || undefined;
  let prompt = String(params.prompt || params.titlePrompt || '').trim();
  if (!prompt && titlePresetId) {
    prompt = await resolveCopywritingTemplateContentById(titlePresetId);
  }
  const language = String(params.copywritingLanguage || DEFAULT_AI_COPYWRITING_LANGUAGE).trim()
    || DEFAULT_AI_COPYWRITING_LANGUAGE;
  const maxCharsRaw = Number(String(params.maxChars ?? '').replace(/\D/g, ''));
  const maxChars = Number.isFinite(maxCharsRaw) && maxCharsRaw > 0
    ? Math.max(1, Math.min(AI_COPYWRITING_MAX_CHARS_LIMIT, Math.floor(maxCharsRaw)))
    : undefined;

  const referenceUrls = [];
  const imageGroups = [];
  for (const source of sources) {
    const ref = await resolvePipelineImageUrl(source.url);
    if (!ref) continue;
    referenceUrls.push(ref);
    imageGroups.push({ urls: [ref], previews: [ref], names: ['商品图 1'] });
  }
  if (imageGroups.length === 0) throw new Error(i18n.t('workbench:ipeUtilError.aiCopyMissingGoods'));

  const useCustomGroups = imageGroups.length > 1;
  const estimatedTotal = useCustomGroups ? imageGroups.length : 1;
  const referenceSpaceIds = sources.map((s) => s.spaceId).filter(Boolean);

  const { taskId, initialResult } = await beginWorkflowFeatureTask({
    taskType: 'aiCopywriting',
    taskName: 'AI文案',
    imageIds: referenceSpaceIds,
    previewUrl: referenceUrls[0] || '',
    estimatedTotal,
    workflowContext,
    initialResult: {
      prompt,
      titlePresetContent: prompt,
      titlePresetId,
      uploadMode: useCustomGroups ? 'custom' : 'default',
      ...buildPipelineReferenceResult({
        imageUrl: referenceUrls[0] || '',
        imageId: referenceSpaceIds[0],
        previewUrl: referenceUrls[0] || '',
      }),
      referenceUrls,
      referencePreviews: referenceUrls,
      ...(useCustomGroups ? { imageGroups } : {}),
      generationMode: 'vision',
      outputCount: 1,
      language,
      languageLabel: getCopywritingLanguageLabel(language),
      ...(maxChars ? { maxChars } : {}),
      content: '',
      text: '',
      serverWorker: true,
      batchPhase: 'queued',
    },
  });

  try {
    await taskAPI.submit(taskId);
    const task = await waitForFundingTaskComplete(taskId);
    const status = String(task?.status || '').toLowerCase();
    if (status === 'failed' || status === 'canceled' || status === 'cancelled') {
      throw new Error(task?.result?.failureHint || i18n.t('workbench:ipeUtilError.aiCopyFailed'));
    }
    const rawContent = String(task?.result?.content || task?.result?.text || '').trim();
    if (!rawContent) throw new Error(i18n.t('workbench:ipeUtilError.aiCopyDoneNoContent'));
    const blocks = rawContent.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
    const bySource = sources.map((source, i) => {
      const content = useCustomGroups
        ? sanitizeCopywritingContent(blocks[i] || '')
        : sanitizeCopywritingContent(rawContent);
      return {
        rowId: source.rowId,
        taskId,
        content,
        language,
        failed: !content,
        error: content ? undefined : i18n.t('workbench:ipeUtilError.rowNoCopy'),
        note: content
          ? (content.length > 120 ? `${content.slice(0, 120)}…` : content)
          : i18n.t('workbench:ipeUtilError.rowNoCopy'),
      };
    });
    const successResult = {
      ...initialResult,
      ...(task?.result && typeof task.result === 'object' ? task.result : {}),
      content: rawContent,
      text: rawContent,
      engine: task?.result?.engine || initialResult.engine || 'qwen',
    };
    await taskAPI.update(taskId, {
      status: 'success',
      successCount: estimatedTotal,
      failedCount: 0,
      total: estimatedTotal,
      finishedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      result: successResult,
    });
    return { taskId, bySource };
  } catch (error) {
    await taskAPI.update(taskId, {
      status: 'failed',
      successCount: 0,
      failedCount: estimatedTotal,
      finishedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      result: {
        ...initialResult,
        failureHint: error?.message || String(error),
      },
    });
    throw error;
  }
}
